/**
 * App Auth Context
 * 
 * Wraps Auth0 and provides app token management as a singleton.
 * This ensures token swap only happens once, regardless of how many
 * components use the useAuth hook or API calls are made.
 */

import { createContext, useContext, useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useAuth0 } from '@auth0/auth0-react'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

// App token refresh margin - refresh 3 minutes before expiry (for 15 min tokens)
const APP_TOKEN_REFRESH_MARGIN = 3 * 60 * 1000

type LoginOptions = {
  returnTo?: string
  screenHint?: 'signup'
}

interface AppToken {
  token: string
  expiresAt: number
}

interface AppAuthContextType {
  isAuthenticated: boolean
  isLoading: boolean
  user: ReturnType<typeof useAuth0>['user']
  error: ReturnType<typeof useAuth0>['error']
  login: (options?: LoginOptions) => void
  logout: () => void
  getAccessToken: () => Promise<string | null>
  refreshAccessToken: () => Promise<string | null>
  hasAppToken: () => boolean
}

const AppAuthContext = createContext<AppAuthContextType | null>(null)

export function AppAuthProvider({ children }: { children: ReactNode }) {
  const {
    isAuthenticated,
    isLoading,
    user,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
    error,
  } = useAuth0()

  // App token state - shared across all useAuth consumers
  const [appToken, setAppToken] = useState<AppToken | null>(null)
  
  // Track swap state - CRITICAL: use promise ref to share in-flight swaps
  const swapPromiseRef = useRef<Promise<AppToken | null> | null>(null)
  const hasAttemptedSwapRef = useRef(false)
  const profileRequiredRef = useRef(false)
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const authorizationParams = {
    audience: import.meta.env.VITE_AUTH0_AUDIENCE,
    scope: 'openid profile email offline_access',
  } as const

  /**
   * Get Auth0 access token (used for profile creation and token swap)
   */
  const getAuth0Token = useCallback(async (): Promise<string | null> => {
    if (!isAuthenticated) {
      return null
    }

    try {
      const token = await getAccessTokenSilently({
        authorizationParams,
      })
      return token
    } catch (err) {
      console.error('Failed to get Auth0 token:', err)
      if (err instanceof Error && err.message.includes('login_required')) {
        loginWithRedirect()
      }
      return null
    }
  }, [isAuthenticated, getAccessTokenSilently, loginWithRedirect, authorizationParams])

  /**
   * Swap Auth0 token for app token.
   * Uses promise sharing so concurrent calls all wait for the same swap.
   */
  const swapToken = useCallback(async (): Promise<AppToken | null> => {
    // If swap is already in progress, return the existing promise
    if (swapPromiseRef.current) {
      console.log('[Auth] Swap in progress, waiting for existing swap...')
      return swapPromiseRef.current
    }

    // Start new swap - create and store the promise IMMEDIATELY
    console.log('[Auth] Starting token swap...')
    
    const swapPromise = (async (): Promise<AppToken | null> => {
      try {
        const auth0Token = await getAuth0Token()
        if (!auth0Token) {
          return null
        }

        const response = await fetch(`${API_BASE_URL}/auth/token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth0Token}`,
          },
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          
          if (response.status === 400 && errorData.code === 'PROFILE_REQUIRED') {
            console.log('[Auth] Profile required - cannot swap token yet')
            profileRequiredRef.current = true
            return null
          }
          
          throw new Error(errorData.error || `Token swap failed: ${response.status}`)
        }

        const data = await response.json()
        const newAppToken: AppToken = {
          token: data.token,
          expiresAt: data.expiresAt,
        }

        console.log('[Auth] Token swap successful')
        profileRequiredRef.current = false
        setAppToken(newAppToken)
        return newAppToken
      } catch (err) {
        console.error('[Auth] Token swap failed:', err)
        return null
      } finally {
        // Clear the promise ref when done
        swapPromiseRef.current = null
      }
    })()

    // Store the promise BEFORE awaiting so concurrent calls can share it
    swapPromiseRef.current = swapPromise
    
    return swapPromise
  }, [getAuth0Token])

  /**
   * Setup automatic app token refresh
   */
  const setupAppTokenRefresh = useCallback((token: AppToken) => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current)
      refreshTimeoutRef.current = null
    }

    const expiresAtMs = token.expiresAt * 1000
    const refreshAtMs = expiresAtMs - APP_TOKEN_REFRESH_MARGIN
    const timeUntilRefresh = refreshAtMs - Date.now()

    if (timeUntilRefresh > 0) {
      console.log(`[Auth] Scheduling token refresh in ${Math.round(timeUntilRefresh / 1000)}s`)
      refreshTimeoutRef.current = setTimeout(async () => {
        console.log('[Auth] Auto-refreshing app token...')
        const newToken = await swapToken()
        if (newToken) {
          setupAppTokenRefresh(newToken)
        }
      }, timeUntilRefresh)
    }
  }, [swapToken])

  /**
   * Get a valid access token for API calls.
   * All concurrent calls share the same swap if one is in progress.
   */
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!isAuthenticated) {
      return null
    }

    // If we have a valid app token, use it
    if (appToken && appToken.expiresAt * 1000 > Date.now()) {
      return appToken.token
    }

    // If profile is required, don't try to swap
    if (profileRequiredRef.current) {
      return getAuth0Token()
    }

    // Try to swap for an app token (concurrent calls share the same promise)
    const newAppToken = await swapToken()
    if (newAppToken) {
      setupAppTokenRefresh(newAppToken)
      return newAppToken.token
    }

    // Fallback to Auth0 token
    return getAuth0Token()
  }, [isAuthenticated, appToken, swapToken, setupAppTokenRefresh, getAuth0Token])

  /**
   * Force refresh the app token
   */
  const refreshAccessToken = useCallback(async (): Promise<string | null> => {
    if (!isAuthenticated) {
      return null
    }

    console.log('[Auth] Force refreshing app token...')
    profileRequiredRef.current = false
    
    // Clear current token and any in-progress swap
    setAppToken(null)
    swapPromiseRef.current = null

    const newAppToken = await swapToken()
    if (newAppToken) {
      setupAppTokenRefresh(newAppToken)
      return newAppToken.token
    }

    return getAuth0Token()
  }, [isAuthenticated, swapToken, setupAppTokenRefresh, getAuth0Token])

  /**
   * Check if we have a valid app token
   */
  const hasAppToken = useCallback((): boolean => {
    return appToken !== null && appToken.expiresAt * 1000 > Date.now()
  }, [appToken])

  // Attempt initial token swap when authenticated (ONCE)
  useEffect(() => {
    if (!isAuthenticated || hasAttemptedSwapRef.current || appToken) {
      return
    }

    hasAttemptedSwapRef.current = true
    console.log('[Auth] Attempting initial token swap...')

    swapToken().then((newToken) => {
      if (newToken) {
        setupAppTokenRefresh(newToken)
      }
    })
  }, [isAuthenticated, appToken, swapToken, setupAppTokenRefresh])

  // Reset state when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      hasAttemptedSwapRef.current = false
      profileRequiredRef.current = false
      swapPromiseRef.current = null
      setAppToken(null)
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current)
        refreshTimeoutRef.current = null
      }
    }
  }, [isAuthenticated])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current)
      }
    }
  }, [])

  const login = useCallback((options?: LoginOptions) => {
    const returnTo = options?.returnTo
    const screenHint = options?.screenHint

    if (!returnTo && !screenHint) {
      loginWithRedirect()
      return
    }

    loginWithRedirect({
      appState: returnTo ? { returnTo } : undefined,
      authorizationParams: {
        ...authorizationParams,
        ...(screenHint ? { screen_hint: screenHint } : {}),
      },
    })
  }, [loginWithRedirect, authorizationParams])

  const logout = useCallback(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current)
      refreshTimeoutRef.current = null
    }

    setAppToken(null)
    hasAttemptedSwapRef.current = false
    profileRequiredRef.current = false
    swapPromiseRef.current = null

    auth0Logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    })
  }, [auth0Logout])

  return (
    <AppAuthContext.Provider value={{
      isAuthenticated,
      isLoading,
      user,
      error,
      login,
      logout,
      getAccessToken,
      refreshAccessToken,
      hasAppToken,
    }}>
      {children}
    </AppAuthContext.Provider>
  )
}

export function useAppAuth(): AppAuthContextType {
  const context = useContext(AppAuthContext)
  if (!context) {
    throw new Error('useAppAuth must be used within AppAuthProvider')
  }
  return context
}

